import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import PartChatDialog from "@/components/PartChatDialog";
import {
  fetchMessages,
  fetchSellerById,
  updateIsRead
} from "../store/partChat/partChatActions";
import { Loader2 } from "lucide-react";
import { toast } from "../components/ui/use-toast";
import { openAuthDialog } from "../store/auth/authSlice";

const InquiryChatHandler = ({ onOpenChange: controlledOnOpenChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { messages, isMessagesLoading } = useSelector(
    (state) => state.partChat
  );
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasShownLoginPrompt, setHasShownLoginPrompt] = useState(false);

  const openChat = searchParams.get("openChat");
  const buyerId = searchParams.get("buyerId");
  const partName = searchParams.get("part");

  useEffect(() => {
    const shouldOpenChat = openChat === "true" && buyerId && partName;

    if (!shouldOpenChat) {
      return;
    }

    if (!isAuthenticated || !user) {
      if (!hasShownLoginPrompt) {
        setHasShownLoginPrompt(true);
        setTimeout(() => {
          toast({
            title: "Login Required!",
            variant: "destructive",
            description:
              "You must be logged in as a Seller to chat with a buyer."
          });
          dispatch(
            openAuthDialog({
              showLoginFirstOrOnSuccess: true,
              redirectPage: window.location.pathname + window.location.search,
              onSuccessfulAuth: () => {
                setHasShownLoginPrompt(false);
              }
            })
          );
        }, 500);
      }
      return;
    }

    if (user.role === "buyer") {
      toast({
        title: "Access Denied!",
        variant: "destructive",
        description: "You must be logged in as a Seller to chat with a buyer."
      });
      return;
    }

    if (user.role === "seller" && isAuthenticated) {
      const sellerId = user.uid;
      handleOpenChat(buyerId, sellerId);
    }
  }, [openChat, buyerId, partName, isAuthenticated, user, hasShownLoginPrompt]);

  const handleOpenChat = async (buyerId, sellerId) => {
    if (isLoading || selectedConversation) {
      return;
    }

    try {
      setIsLoading(true);

      const otherUserDetail = await dispatch(
        fetchSellerById({ userId: buyerId })
      ).unwrap();

      const conversation = {
        partInfo: {
          id: partName.toLowerCase().replace(/\s+/g, "-"),
          name: partName || "General Inquiry"
        },
        otherUserDetail,
        isInquiryChat: true
      };

      dispatch(
        fetchMessages({
          buyerId: otherUserDetail?.id || otherUserDetail?.uid,
          sellerId: user?.id || user?.uid,
          partId: partName.toLowerCase().replace(/\s+/g, "-"),
          currentUserId: user.uid
        })
      );

      setSelectedConversation(conversation);
      setIsOpen(true);
    } catch (error) {
      console.error("Failed to open chat:", error);
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to open chat. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseChat = () => {
    setSelectedConversation(null);
    setHasShownLoginPrompt(false);
    searchParams.delete("part");
    searchParams.delete("openChat");
    searchParams.delete("buyerId");
    setSearchParams(searchParams);
  };

  if (openChat !== "true" || !buyerId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-background p-6 rounded-lg shadow-xl flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Opening chat...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {selectedConversation && (
        <PartChatDialog
          messages={messages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={false}
          onOpenChange={(open) => {
            if (!open) handleCloseChat();
          }}
        >
          <></>
        </PartChatDialog>
      )}
    </>
  );
};

export default InquiryChatHandler;
